import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const StaffPollManagement = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [polls, setPolls] = useState([]);
    const [activePoll, setActivePoll] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPoll, setEditingPoll] = useState(null);
    const [formData, setFormData] = useState({ question: '', options: [{ text: '', isCorrect: false }, { text: '', isCorrect: false }] });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchPolls();
        fetchActivePoll();
        const interval = setInterval(fetchActivePoll, 3000);
        return () => clearInterval(interval);
    }, []);

    const fetchPolls = async () => {
        try {
            const res = await api.get('/polls/all');
            setPolls(res.data);
        } catch (error) {
            console.error("Error fetching polls", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchActivePoll = async () => {
        try {
            const res = await api.get('/polls/active');
            setActivePoll(res.data);
        } catch (error) {
            console.error("Error fetching active poll", error);
        }
    };

    const openCreateModal = () => {
        setEditingPoll(null);
        setFormData({ question: '', options: [{ text: '', isCorrect: false }, { text: '', isCorrect: false }] });
        setIsModalOpen(true);
    };

    const openEditModal = async (poll) => {
        try {
            const res = await api.get(`/polls/${poll.id}`);
            const fullPoll = res.data;
            setEditingPoll(fullPoll);
            setFormData({
                question: fullPoll.question,
                options: fullPoll.options.map(o => ({
                    text: o.option_text,
                    isCorrect: !!o.is_correct
                }))
            });
            setIsModalOpen(true);
        } catch (error) {
            console.error("Error fetching poll details", error);
        }
    };

    const handleSavePoll = async (e) => {
        e.preventDefault();
        const filteredOptions = formData.options.filter(o => o.text.trim() !== '');
        if (filteredOptions.length < 2) return alert("Mínimo 2 opciones");

        // Prepare data for backend
        const submissionData = {
            question: formData.question,
            sala_id: editingPoll ? editingPoll.sala_id : (user?.sala_id || null),
            options: filteredOptions.map(o => ({
                option_text: o.text,
                is_correct: o.isCorrect
            }))
        };

        try {
            if (editingPoll) {
                await api.put(`/polls/${editingPoll.id}`, submissionData);
            } else {
                await api.post('/polls', submissionData);
            }
            setIsModalOpen(false);
            fetchPolls();
        } catch (error) {
            console.error("Error saving poll", error);
        }
    };

    const toggleStatus = async (id, status) => {
        try {
            await api.put(`/polls/${id}/status`, { status });
            fetchPolls();
            fetchActivePoll();
        } catch (error) {
            console.error("Error toggling status", error);
        }
    };

    const addOption = () => setFormData({ ...formData, options: [...formData.options, { text: '', isCorrect: false }] });
    const removeOption = (index) => {
        if (formData.options.length <= 2) return;
        setFormData({ ...formData, options: formData.options.filter((_, i) => i !== index) });
    };

    const setCorrectOption = (index) => {
        const newOptions = formData.options.map((opt, i) => ({
            ...opt,
            isCorrect: i === index
        }));
        setFormData({ ...formData, options: newOptions });
    };

    const viewStats = (poll) => {
        navigate(`/interactiva-stats/${poll.id}`);
    };

    return (
        <div className="animate-fadeIn space-y-10 pb-20">
            <header className="flex justify-between items-center bg-white p-6 rounded-3xl shadow-xl">
                <div>
                    <h1
                        className="text-3xl font-black uppercase tracking-tighter italic"
                        style={{
                            background: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            color: 'transparent'
                        }}
                    >
                        Panel de Trivia
                    </h1>
                    <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mt-2">{activePoll ? '● Activa Ahora' : '● Sin trivia pública'}</p>
                </div>
                <button
                    onClick={openCreateModal}
                    className="bg-purple-600 text-white px-8 py-4 rounded-3xl font-black text-[11px] uppercase tracking-widest hover:scale-105 active:scale-95 transition-all shadow-2xl shadow-purple-900/40"
                >
                    + Nueva Pregunta
                </button>
            </header>

            <section className="space-y-8">
                <div className="flex justify-between items-center px-4 border-l-2 border-purple-600">
                    <h3 className="text-sm font-black text-white/60 uppercase tracking-[0.3em] italic">Catálogo de Trivia</h3>
                    <span className="text-[10px] font-black text-white/20 uppercase tracking-widest">{polls.length} preguntas en total</span>
                </div>

                <div className="grid gap-6">
                    {polls.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 bg-white/5 rounded-[40px] border border-dashed border-white/10">
                            <span className="text-5xl mb-6 opacity-20">📊</span>
                            <p className="text-white/20 text-xs font-black uppercase tracking-widest italic">No hay preguntas registradas aún.</p>
                        </div>
                    ) : polls.map(p => (
                        <div key={p.id} className={`bg-white/5 border border-white/10 p-8 rounded-[35px] flex flex-col md:flex-row justify-between items-start md:items-center gap-6 group hover:bg-white/10 hover:border-white/20 transition-all duration-500 relative overflow-hidden ${p.status === 'active' ? 'ring-2 ring-emerald-500/30' : ''}`}>
                            <div className="max-w-2xl relative z-10">
                                <p
                                    className="text-lg font-black mb-4 italic uppercase tracking-tighter leading-none"
                                    style={{
                                        background: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        color: 'transparent'
                                    }}
                                >{p.question}</p>
                                <div className="flex items-center gap-4">
                                    <span className={`text-[9px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full ${p.status === 'active' ? 'bg-emerald-500 text-white' :
                                        p.status === 'closed' ? 'bg-white/10 text-white/40' : 'bg-purple-500 text-white'
                                        }`}>
                                        {p.status === 'active' ? 'Activa' : p.status === 'closed' ? 'Finalizada' : 'Borrador'}
                                    </span>
                                    {p.sala_name && (
                                        <span className="text-[9px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full bg-indigo-500/20 text-indigo-300">
                                            Sala: {p.sala_name}
                                        </span>
                                    )}
                                    <span className="text-[9px] font-black text-white/20 uppercase tracking-widest">Creada: {new Date(p.created_at).toLocaleDateString()}</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 w-full md:w-auto relative z-10">
                                <button
                                    onClick={() => viewStats(p)}
                                    className="flex-grow md:flex-none flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-white/5 border border-white/5 text-white/60 text-[10px] font-black uppercase tracking-widest hover:bg-white/10 hover:text-white transition-all"
                                    style={{
                                        background: 'linear-gradient(to right, #f2536d, #f77a55, #ffb133)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        color: 'transparent'
                                    }}
                                >
                                    📊 Stats
                                </button>
                                <button
                                    onClick={() => openEditModal(p)}
                                    className="px-4 py-3 rounded-2xl bg-white/5 border border-white/5 text-white/40 text-[10px] font-black uppercase hover:text-blue-400 transition-all"
                                    style={{
                                        background: 'linear-gradient(to right, #f2536d, #f77a55, #ffb133)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        color: 'transparent'
                                    }}
                                >
                                    ✎
                                </button>
                                {p.status !== 'active' ? (
                                    <button
                                        onClick={() => toggleStatus(p.id, 'active')}
                                        className="flex-grow md:flex-none bg-emerald-600 text-white px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-500 active:scale-95 transition-all shadow-xl shadow-emerald-900/40"
                                    >
                                        Publicar
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => toggleStatus(p.id, 'closed')}
                                        className="flex-grow md:flex-none bg-red-600 text-white px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-red-500 active:scale-95 transition-all shadow-xl shadow-red-900/40"
                                    >
                                        Finalizar
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Modal de Creación/Edición */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 animate-fadeIn">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" onClick={() => setIsModalOpen(false)}></div>

                    <div className="bg-[var(--bg-deep-purple)] border border-white/20 w-full max-w-2xl rounded-[50px] p-12 relative z-10 shadow-[0_0_100px_rgba(157,78,221,0.3)] animate-slideUp max-h-[90vh] overflow-y-auto custom-scrollbar">
                        <header className="mb-10">
                            <h2 className="text-3xl font-black text-white italic uppercase tracking-tighter">
                                {editingPoll ? 'Editar Pregunta' : 'Nueva Pregunta Trivia'}
                            </h2>
                            <p className="text-white/40 text-[10px] font-black uppercase tracking-widest mt-2 italic">Define el desafío para sus asistentes</p>
                        </header>

                        <form onSubmit={handleSavePoll} className="space-y-8">
                            <div>
                                <label className="block text-white/20 text-[9px] font-black uppercase tracking-widest mb-4 ml-4">Pregunta Central</label>
                                <div className="p-[2px] rounded-[42px] bg-gradient-to-r from-[#ffa800] to-[#ff5e00]">
                                    <div className="w-full bg-white rounded-[40px] shadow-inner">
                                        <textarea
                                            className="w-full bg-transparent p-8 rounded-[40px] text-lg font-black italic uppercase tracking-tight focus:bg-slate-50 transition-all outline-none min-h-[120px]"
                                            value={formData.question}
                                            onChange={e => setFormData({ ...formData, question: e.target.value })}
                                            placeholder="¿CUÁL ES EL SIGUIENTE PASO CLÍNICO?"
                                            required
                                            style={{
                                                background: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                backgroundClip: 'text',
                                                color: 'transparent'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <label className="flex justify-between items-center text-white/20 text-[9px] font-black uppercase tracking-widest mb-4 ml-4"
                                    style={{
                                        background: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        color: 'transparent'
                                    }}>
                                    Opciones de Respuesta
                                    <button type="button" onClick={addOption} className="text-blue-400 hover:text-white transition-all text-[10px]"
                                        style={{
                                            background: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                            color: 'transparent'
                                        }}>+ Añadir Opción</button>
                                </label>

                                {formData.options.map((opt, i) => (
                                    <div key={i} className="flex gap-4 items-center group">
                                        <div className="flex flex-col items-center gap-1 min-w-[60px]">
                                            <input
                                                type="radio"
                                                name="correct_option"
                                                checked={opt.isCorrect}
                                                onChange={() => setCorrectOption(i)}
                                                className="w-5 h-5 accent-emerald-500 cursor-pointer"
                                                required
                                                style={{
                                                    background: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
                                                    WebkitBackgroundClip: 'text',
                                                    WebkitTextFillColor: 'transparent',
                                                    backgroundClip: 'text',
                                                    color: 'transparent'
                                                }}
                                            />
                                            <span className="text-[8px] font-black text-slate-400 uppercase">Correcta</span>
                                        </div>
                                        <input
                                            className="flex-grow bg-white border border-slate-200 p-5 rounded-3xl font-bold text-sm focus:border-purple-500 transition-all outline-none"
                                            value={opt.text}
                                            onChange={e => {
                                                const opts = [...formData.options];
                                                opts[i].text = e.target.value;
                                                setFormData({ ...formData, options: opts });
                                            }}
                                            placeholder={`OPCIÓN ${i + 1}`}
                                            required
                                            style={{
                                                background: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                backgroundClip: 'text',
                                                color: 'transparent'
                                            }}
                                        />
                                        {formData.options.length > 2 && (
                                            <button
                                                type="button"
                                                onClick={() => removeOption(i)}
                                                className="text-red-500/50 hover:text-red-500 transition-all font-black text-xs px-4"
                                                style={{
                                                    background: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
                                                    WebkitBackgroundClip: 'text',
                                                    WebkitTextFillColor: 'transparent',
                                                    backgroundClip: 'text',
                                                    color: 'transparent'
                                                }}
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="flex gap-6 pt-6">
                                <div className="flex-grow p-[2px] rounded-[24px] bg-gradient-to-r from-[#ffa800] to-[#ff5e00]">
                                    <div className="w-full bg-white rounded-[22px]">
                                        <button
                                            type="button"
                                            onClick={() => setIsModalOpen(false)}
                                            className="w-full py-5 rounded-[22px] bg-transparent font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 transition-all"
                                            style={{
                                                background: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                backgroundClip: 'text',
                                                color: 'transparent'
                                            }}
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </div>
                                <div className="flex-grow p-[2px] rounded-[24px] bg-gradient-to-r from-[#ffa800] to-[#ff5e00]">
                                    <div className="w-full bg-white rounded-[22px]">
                                        <button 
                                            type="submit" 
                                            className="w-full bg-transparent py-5 rounded-[22px] font-black text-[11px] uppercase tracking-widest shadow-lg hover:scale-[1.02] transition-all"
                                            style={{
                                                background: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                backgroundClip: 'text',
                                                color: 'transparent'
                                            }}
                                        >
                                            {editingPoll ? 'Guardar Cambios' : 'Crear Pregunta'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StaffPollManagement;
